/**
 * SigningPage  — /sign/:token
 *
 * The customer-facing document-signing page. Completely standalone: no App
 * Bridge, no Polaris, no session token required. Access is controlled by the
 * opaque per-session token embedded in the URL.
 *
 * States:
 *   loading      → fetching session + PDF
 *   error        → expired / not-found / declined / already-signed
 *   signing      → interactive PDF + field overlays
 *   submitting   → POST in progress (spinner overlay)
 *   complete     → signed successfully (confirmation view)
 *   declined     → user clicked Decline
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page } from 'react-pdf';
import SignaturePad from '../components/SignaturePad';
import type { ConditionalArea, FieldType, SessionView, TemplateField } from '../types';
import { activeFieldIds } from '../fieldConditions';

// ─── global page styles (injected once) ──────────────────────────────────────

const GLOBAL_CSS = `
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f3f4f6; color: #111827; }
  input, button, textarea, select { font-family: inherit; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }
`;

function injectStyles() {
  if (document.getElementById('signing-global')) return;
  const el = document.createElement('style');
  el.id = 'signing-global';
  el.textContent = GLOBAL_CSS;
  document.head.appendChild(el);
}

// ─── types ────────────────────────────────────────────────────────────────────

type PageStatus = 'loading' | 'error' | 'signing' | 'submitting' | 'complete' | 'declined';

interface FieldValues {
  [fieldId: string]: { value?: string; signatureDataUrl?: string };
}

// ─── colour helpers ───────────────────────────────────────────────────────────

const FIELD_TYPE_COLOR: Record<FieldType, string> = {
  SIGNATURE: '#7C3AED',
  INITIALS: '#6D28D9',
  TEXT: '#2563EB',
  NAME: '#1D4ED8',
  EMAIL: '#0369A1',
  DATE: '#0891B2',
  CHECKBOX: '#059669',
};

// ─── FieldOverlay ─────────────────────────────────────────────────────────────

interface OverlayProps {
  field: TemplateField;
  value?: { value?: string; signatureDataUrl?: string };
  pageHeightPx: number;
  brandColor: string;
  invalid: boolean;
  onTextChange: (fieldId: string, value: string) => void;
  onSignatureClick: (field: TemplateField) => void;
}

function FieldOverlay({
  field,
  value,
  pageHeightPx,
  brandColor,
  invalid,
  onTextChange,
  onSignatureClick,
}: OverlayProps) {
  const color = FIELD_TYPE_COLOR[field.type] ?? brandColor;
  const fieldHeightPx = (field.height / 100) * pageHeightPx;
  // Slightly larger font so values are readable relative to the document text
  const fontSize = Math.max(10, Math.min(14, fieldHeightPx * 0.58));

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${field.posX}%`,
    top: `${field.posY}%`,
    width: `${field.width}%`,
    height: `${field.height}%`,
    boxSizing: 'border-box',
  };

  const filled = !!(value?.value || value?.signatureDataUrl);
  const errColor = '#EF4444';

  // ── SIGNATURE / INITIALS ──────────────────────────────────────────────────
  if (field.type === 'SIGNATURE' || field.type === 'INITIALS') {
    return (
      <div
        id={`field-${field.id}`}
        onClick={() => onSignatureClick(field)}
        role="button"
        aria-label={`${field.label}: click to sign`}
        style={{
          ...baseStyle,
          // Filled: solid white so the PDF's placeholder chip is fully covered;
          // a thin green bottom border signals completion.
          // Empty: dashed hint box with subtle tint.
          background: filled ? '#ffffff' : `${invalid ? errColor : color}0c`,
          border: filled
            ? `none`
            : `2px dashed ${invalid ? errColor : color}88`,
          borderBottom: filled ? `2px solid #059669` : undefined,
          borderRadius: filled ? 0 : 3,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {value?.signatureDataUrl ? (
          <img
            src={value.signatureDataUrl}
            alt="Signature"
            style={{ maxWidth: '98%', maxHeight: '98%', objectFit: 'contain', pointerEvents: 'none' }}
          />
        ) : (
          <span style={{
            fontSize: Math.max(9, fontSize * 0.8),
            color: invalid ? errColor : color,
            fontWeight: 500,
            textAlign: 'center',
            padding: '0 4px',
            lineHeight: 1.2,
            opacity: 0.85,
          }}>
            {field.type === 'SIGNATURE' ? '✍ Sign here' : 'Initials'}
          </span>
        )}
      </div>
    );
  }

  // ── CHECKBOX ──────────────────────────────────────────────────────────────
  if (field.type === 'CHECKBOX') {
    const checkSizePx = Math.min(Math.max(14, fieldHeightPx * 0.65), 22);
    return (
      <div
        id={`field-${field.id}`}
        style={{
          ...baseStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // White background covers any baked-in placeholder chip in the PDF
          background: '#ffffff',
        }}
      >
        <input
          type="checkbox"
          checked={value?.value === 'true'}
          onChange={(e) => onTextChange(field.id, e.target.checked ? 'true' : 'false')}
          style={{ width: checkSizePx, height: checkSizePx, cursor: 'pointer', accentColor: color }}
          aria-label={field.label}
        />
      </div>
    );
  }

  // ── TEXT / NAME / EMAIL / DATE ────────────────────────────────────────────
  const inputType =
    field.type === 'DATE' ? 'date' : field.type === 'EMAIL' ? 'email' : 'text';

  return (
    <div id={`field-${field.id}`} style={baseStyle}>
      <input
        type={inputType}
        value={value?.value ?? ''}
        onChange={(e) => onTextChange(field.id, e.target.value)}
        // Only show the placeholder hint when empty; suppress it when filled
        // (the HTML placeholder attribute already hides when value is set, but
        // setting it empty makes the intent explicit for date inputs)
        placeholder={filled ? '' : (field.placeholder ?? field.label)}
        aria-label={field.label}
        style={{
          width: '100%',
          height: '100%',
          // Filled: signature-line style — opaque white background completely
          // covers the PDF's baked-in placeholder chip text; only a bottom
          // border (underline) remains, giving a clean "filled-in document" look.
          // Empty: subtle dashed hint box so the signer can see where to type.
          padding: filled ? '0 3px 1px' : '0 4px',
          border: filled ? 'none' : `1.5px dashed ${invalid ? errColor : color}55`,
          borderBottom: filled
            ? `2px solid ${invalid ? errColor : color}`
            : undefined,
          borderRadius: filled ? 0 : 2,
          background: filled ? '#ffffff' : (invalid ? `${errColor}06` : `${color}07`),
          fontSize,
          fontWeight: filled ? 500 : 400,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          color: '#111827',
          outline: 'none',
          // colorScheme keeps the native date-picker chrome light on all browsers
          colorScheme: 'light',
        } as React.CSSProperties}
        onFocus={(e) => {
          if (!filled) {
            e.target.style.borderColor = invalid ? errColor : color;
            e.target.style.background = `${color}0f`;
          }
        }}
        onBlur={(e) => {
          if (!filled) {
            e.target.style.borderColor = invalid ? errColor : `${color}55`;
            e.target.style.background = invalid ? `${errColor}06` : `${color}07`;
          }
        }}
      />
    </div>
  );
}

// ─── Another signer's field (multi-signer documents) ─────────────────────────

/** Read-only: shows what an earlier signer entered, or who will fill it later. */
function OtherSignerField({ field, done, signerName }: {
  field: TemplateField;
  done?: { value: string | null; signatureDataUrl: string | null };
  signerName: string;
}) {
  const style: React.CSSProperties = {
    position: 'absolute',
    left: `${field.posX}%`,
    top: `${field.posY}%`,
    width: `${field.width}%`,
    height: `${field.height}%`,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: done?.signatureDataUrl ? 'center' : 'flex-start',
    overflow: 'hidden',
    lineHeight: 1.2,
  };
  if (done?.signatureDataUrl || done?.value) {
    return (
      <div style={{ ...style, background: '#ffffff', borderBottom: '2px solid #9ca3af' }} title={`Filled in by ${signerName}`}>
        {done.signatureDataUrl ? (
          <img src={done.signatureDataUrl} alt={`Signature of ${signerName}`}
            style={{ maxWidth: '98%', maxHeight: '98%', objectFit: 'contain' }} />
        ) : (
          <span style={{ fontSize: 12, color: '#374151', padding: '0 3px', whiteSpace: 'nowrap' }}>
            {done.value === 'true' ? '☑' : done.value === 'false' ? '☐' : done.value}
          </span>
        )}
      </div>
    );
  }
  return (
    <div style={{ ...style, justifyContent: 'center', background: '#f3f4f6cc', border: '1.5px dashed #9ca3af', borderRadius: 3 }}
      title={`${field.label} — for ${signerName}`}>
      <span style={{ fontSize: 10, color: '#6b7280', textAlign: 'center', padding: '0 3px' }}>
        For {signerName}
      </span>
    </div>
  );
}

// ─── Conditional section cover ────────────────────────────────────────────────

/**
 * Covers a section that doesn't apply (its checkbox isn't ticked). The text is
 * only shown when the checkbox itself is visible; covers of nested sections
 * under an already covered one stay plain.
 */
function SectionCover({ area, boxLabel, mine }: {
  area: ConditionalArea;
  boxLabel?: string;
  mine: boolean;
}) {
  return (
    <div
      data-testid="section-cover"
      style={{
        position: 'absolute', left: `${area.posX}%`, top: `${area.posY}%`, width: `${area.width}%`, height: `${area.height}%`,
        boxSizing: 'border-box', background: 'rgba(241,245,249,0.97)', border: '1.5px dashed #cbd5e1', borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 8,
        pointerEvents: 'none', lineHeight: 1.35,
      }}
    >
      {boxLabel !== undefined && (
        <span style={{ fontSize: 13, color: '#475569', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '5px 12px' }}>
          {mine
            ? <>This part only applies if you tick <strong>☑ {boxLabel}</strong></>
            : <>Doesn't apply — <strong>{boxLabel}</strong> was not ticked</>}
        </span>
      )}
    </div>
  );
}

// ─── Error screen ─────────────────────────────────────────────────────────────

function ErrorScreen({
  message,
  icon,
  brandColor,
}: {
  message: string;
  icon: string;
  brandColor: string;
}) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>{icon}</div>
      <p style={{ fontSize: '18px', color: '#374151', maxWidth: '380px', lineHeight: 1.5 }}>{message}</p>
      <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '24px' }}>Secured by SignShield</p>
    </div>
  );
}

// ─── Expired link screen ──────────────────────────────────────────────────────

/** Who to ask for a new link (sent by the server with a 410). */
interface ExpiredInfo {
  merchantName: string | null;
  contactEmail: string | null;
  brandColor: string | null;
  templateName: string | null;
  orderNumber: string | null;
}

function ExpiredScreen({ info, message }: { info: ExpiredInfo; message: string }) {
  const color = info.brandColor || '#2563EB';
  const store = info.merchantName || 'the store';
  const doc = info.templateName ? `“${info.templateName}”` : 'this document';
  const subject = `New signing link${info.templateName ? ` for ${info.templateName}` : ''}${info.orderNumber ? ` (order #${info.orderNumber})` : ''}`;
  const body = `Hello,

My signing link${info.templateName ? ` for ${info.templateName}` : ''}${info.orderNumber ? ` (order #${info.orderNumber})` : ''} has expired. Could you please send me a new one?

Thank you.`;
  const mailto = info.contactEmail
    ? `mailto:${info.contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    : null;
  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px' }}>
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', maxWidth: 440, width: '100%', padding: '32px 28px', textAlign: 'center' }}>
        <div style={{ fontSize: 44, lineHeight: 1.2, marginBottom: 14 }}>⏰</div>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 10px' }}>
          {/no longer active/i.test(message) ? 'This signing link is no longer active' : 'This signing link has expired'}
        </h1>
        <p style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.55, margin: '0 0 6px' }}>
          The link to sign {doc}{info.orderNumber ? <> for order <strong>#{info.orderNumber}</strong></> : null} can no longer be used.
        </p>
        <p style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.55, margin: '0 0 22px' }}>
          Nothing is lost — just ask <strong>{store}</strong> to send you a new link.
        </p>
        {mailto ? (
          <>
            <a href={mailto} style={{ display: 'inline-block', background: color, color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: 15, padding: '12px 22px', borderRadius: 8 }}>
              Ask {store} for a new link
            </a>
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: 12 }}>
              or email <a href={`mailto:${info.contactEmail}`} style={{ color }}>{info.contactEmail}</a>
            </p>
          </>
        ) : (
          <p style={{ fontSize: 14, color: '#6b7280' }}>Please reply to the email you received from {store}.</p>
        )}
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 24 }}>Secured by SignShield</p>
      </div>
    </div>
  );
}

// ─── Success screen ───────────────────────────────────────────────────────────

function SuccessScreen({
  session,
  merchant,
  templateName,
  signedAt,
  waitingFor,
}: {
  session: SessionView['session'];
  merchant: SessionView['merchant'];
  templateName: string;
  signedAt: Date;
  brandColor: string;
  /** Multi-signer: names still to sign after this signer. */
  waitingFor?: string;
}) {
  const brandColor = merchant.brandColor ?? '#2563EB';
  const merchantName = merchant.companyName ?? merchant.storeName ?? 'The merchant';

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{ background: brandColor, padding: '16px 24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        {merchant.logoUrl && <img src={merchant.logoUrl} alt={merchantName} style={{ height: '32px', objectFit: 'contain' }} />}
        <span style={{ color: '#fff', fontWeight: 700, fontSize: '16px' }}>{merchantName}</span>
      </header>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px' }}>
        <div style={{
          background: '#fff', borderRadius: '16px', padding: '40px 32px',
          maxWidth: '460px', width: '100%', textAlign: 'center',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          animation: 'fadeIn 0.4s ease',
        }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '28px' }}>
            ✓
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 8px', color: '#111827' }}>Document signed</h1>
          <p style={{ color: '#6b7280', margin: '0 0 24px', lineHeight: 1.5 }}>
            You've successfully signed <strong>{templateName}</strong> for{' '}
            <strong>{merchantName}</strong>.
          </p>
          <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '16px', textAlign: 'left', marginBottom: '24px' }}>
            <Row label="Signer" value={session.signerName} />
            <Row label="Email" value={session.signerEmail} />
            <Row label="Signed" value={signedAt.toLocaleString()} />
          </div>
          {waitingFor && (
            <p style={{ fontSize: '14px', color: '#374151', background: '#eff6ff', borderRadius: '8px', padding: '12px', margin: '0 0 16px' }}>
              Next, {waitingFor} will sign. You'll receive the completed document by email once everyone has signed.
            </p>
          )}
          <p style={{ fontSize: '13px', color: '#9ca3af' }}>
            A confirmation email has been sent to {session.signerEmail}. You may close this window.
          </p>
        </div>
      </main>
      <footer style={{ textAlign: 'center', padding: '20px 16px', borderTop: '1px solid #f3f4f6' }}>
        <a href="https://merchantkits.com/signshield" target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '6px', textDecoration: 'none' }}>
          <img src={`${window.location.origin}/logo-full.png`} alt="SignShield" style={{ width: '150px', height: 'auto', opacity: 1 }} />
          <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>Powered by SignShield</span>
        </a>
      </footer>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}

/** The fields this signer fills: all of them, or (multi-signer) only their own. */
/**
 * Conditional fields: ids of the fields shown right now. A field set to "only
 * if ☑ … is ticked" appears when that checkbox is ticked — by this signer
 * (current values) or an earlier one (completed values).
 */
function shownFieldIds(view: SessionView, values: FieldValues): Set<string> {
  const done = new Map((view.completedFields ?? []).map((c) => [c.fieldId, c.value]));
  return activeFieldIds(view.fields, (id) => (id in values ? values[id]?.value : done.get(id)));
}

function ownFields(view: SessionView): TemplateField[] {
  if (!view.signer) return view.fields;
  return view.fields.filter((f) => (f.signerPosition ?? 1) === view.signer!.position);
}

/** "Anna", "Anna and Ben", "Anna, Ben and Carl". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function SigningPage() {
  const { token } = useParams<{ token: string }>();

  const [status, setStatus] = useState<PageStatus>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [expired, setExpired] = useState<ExpiredInfo | null>(null);
  const [sessionView, setSessionView] = useState<SessionView | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageHeights, setPageHeights] = useState<Record<number, number>>({});
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [consentChecked, setConsentChecked] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [signaturePadField, setSignaturePadField] = useState<TemplateField | null>(null);
  const [signedAt, setSignedAt] = useState<Date | null>(null);

  // Responsive PDF width
  const pdfWrapRef = useRef<HTMLDivElement>(null);
  const [pdfWidth, setPdfWidth] = useState(680);

  injectStyles();

  // ── Load session + PDF ──────────────────────────────────────────────────

  useEffect(() => {
    if (!token) return;

    (async () => {
      try {
        const res = await fetch(`/api/sign/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const msg: string = (body as any).message ?? 'Something went wrong.';
          // Map HTTP status to user-friendly messages
          if (res.status === 404) setErrorMsg('This signing link could not be found. It may have been revoked or never existed.');
          else if (res.status === 410) {
            setErrorMsg(msg);
            if ((body as any).link) setExpired((body as any).link as ExpiredInfo);
            else setErrorMsg('This signing link has expired. Please contact the sender for a new link.');
          }
          // Multi-signer: "not your turn yet" is also a 403 — show the server's explanation.
          else if (res.status === 403) setErrorMsg(/turn/i.test(msg) ? msg : 'This document has already been declined.');
          else setErrorMsg(msg);
          setStatus('error');
          return;
        }

        const view: SessionView = await res.json();

        // Session already signed — show a friendly "already done" message
        if (view.session.status === 'SIGNED') {
          setErrorMsg('This document has already been signed. Check your email for the confirmation.');
          setStatus('error');
          return;
        }

        setSessionView(view);

        // Prefill NAME / EMAIL / DATE fields from session context
        const defaults: FieldValues = {};
        for (const field of ownFields(view)) {
          if (field.type === 'NAME') defaults[field.id] = { value: view.session.signerName };
          else if (field.type === 'EMAIL') defaults[field.id] = { value: view.session.signerEmail };
          else if (field.type === 'DATE') defaults[field.id] = { value: new Date().toISOString().slice(0, 10) };
          else if (field.defaultValue) defaults[field.id] = { value: field.defaultValue };
        }
        setFieldValues(defaults);

        // Fetch PDF as an object URL so react-pdf can load it
        const pdfRes = await fetch(`/api/sign/${token}/pdf`);
        if (pdfRes.ok) {
          const blob = await pdfRes.blob();
          setPdfUrl(URL.createObjectURL(blob));
        }

        setStatus('signing');
      } catch {
        setErrorMsg('Failed to load the signing page. Please check your connection and try again.');
        setStatus('error');
      }
    })();

    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Responsive PDF width
  useLayoutEffect(() => {
    if (!pdfWrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setPdfWidth(w);
    });
    ro.observe(pdfWrapRef.current);
    return () => ro.disconnect();
  }, [status]);

  // ── Branding ────────────────────────────────────────────────────────────

  const brandColor = sessionView?.merchant.brandColor ?? '#2563EB';
  const merchantName =
    sessionView?.merchant.companyName ??
    sessionView?.merchant.storeName ??
    'SignShield';

  // ── Field value handlers ────────────────────────────────────────────────

  const onTextChange = useCallback((fieldId: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: { value } }));
    setValidationErrors((prev) => { const n = new Set(prev); n.delete(fieldId); return n; });
  }, []);

  const onSignatureClick = useCallback((field: TemplateField) => {
    setSignaturePadField(field);
  }, []);

  const onSignatureConfirm = useCallback(
    (dataUrl: string) => {
      if (!signaturePadField) return;
      setFieldValues((prev) => ({
        ...prev,
        [signaturePadField.id]: { signatureDataUrl: dataUrl },
      }));
      setValidationErrors((prev) => { const n = new Set(prev); n.delete(signaturePadField.id); return n; });
      setSignaturePadField(null);
    },
    [signaturePadField],
  );

  // ── Validation ──────────────────────────────────────────────────────────

  const validate = (): boolean => {
    if (!sessionView) return false;
    const errors = new Set<string>();
    const shown = shownFieldIds(sessionView, fieldValues);
    for (const field of ownFields(sessionView)) {
      if (!field.required || !shown.has(field.id)) continue;
      const val = fieldValues[field.id];
      if (field.type === 'SIGNATURE' || field.type === 'INITIALS') {
        if (!val?.signatureDataUrl) errors.add(field.id);
      } else if (!val?.value) {
        errors.add(field.id);
      }
    }
    setValidationErrors(errors);
    if (errors.size > 0) {
      // Scroll to first unfilled field
      const firstId = sessionView.fields.find((f) => errors.has(f.id))?.id;
      if (firstId) {
        document.getElementById(`field-${firstId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }
    return true;
  };

  // ── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!sessionView || !token) return;
    if (!consentChecked) {
      document.getElementById('consent-checkbox')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!validate()) return;

    setStatus('submitting');
    try {
      const shown = shownFieldIds(sessionView, fieldValues);
      const fields = ownFields(sessionView).filter((f) => shown.has(f.id)).map((f) => {
        const val = fieldValues[f.id];
        return {
          fieldId: f.id,
          value: val?.value,
          signatureDataUrl: val?.signatureDataUrl,
        };
      });

      const res = await fetch(`/api/sign/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).message ?? `HTTP ${res.status}`);
      }

      const { signedAt: signedAtStr } = await res.json();
      setSignedAt(new Date(signedAtStr));
      setStatus('complete');
    } catch (e: any) {
      setErrorMsg(e.message ?? 'Submission failed. Please try again.');
      setStatus('signing'); // return to form so they can retry
    }
  };

  // ── Decline ─────────────────────────────────────────────────────────────

  const handleDecline = async () => {
    if (!token) return;
    const reason = window.confirm('Are you sure you want to decline this document?');
    if (!reason) return;
    try {
      await fetch(`/api/sign/${token}/decline`, { method: 'POST' });
    } catch { /* best-effort */ }
    setStatus('declined');
  };

  // ── Page height tracking (for overlay coordinate scaling) ────────────────

  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const measurePage = useCallback((pageNum: number) => {
    const el = pageRefs.current[pageNum];
    if (!el) return;
    const canvas = el.querySelector('canvas');
    if (canvas && canvas.offsetHeight > 0) {
      setPageHeights((prev) =>
        prev[pageNum] === canvas.offsetHeight ? prev : { ...prev, [pageNum]: canvas.offsetHeight },
      );
    }
  }, []);

  // ── Render: loading ──────────────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
        <div style={{ width: 36, height: 36, border: `3px solid #e5e7eb`, borderTopColor: '#2563EB', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading document…</p>
      </div>
    );
  }

  // ── Render: error / declined ─────────────────────────────────────────────

  if (status === 'error' && expired) {
    return <ExpiredScreen info={expired} message={errorMsg} />;
  }
  if (status === 'error') {
    return <ErrorScreen message={errorMsg} icon="⚠️" brandColor={brandColor} />;
  }
  if (status === 'declined') {
    return <ErrorScreen message="You have declined to sign this document. The sender has been notified." icon="📋" brandColor={brandColor} />;
  }

  // ── Render: success ──────────────────────────────────────────────────────

  if (status === 'complete' && sessionView && signedAt) {
    const me = sessionView.signer?.position;
    const later = (sessionView.signers ?? []).filter((s) => me !== undefined && s.position > me).map((s) => s.name);
    return (
      <SuccessScreen
        session={sessionView.session}
        merchant={sessionView.merchant}
        templateName={sessionView.template.name}
        signedAt={signedAt}
        brandColor={brandColor}
        waitingFor={later.length ? joinNames(later) : undefined}
      />
    );
  }

  if (!sessionView) return null;

  // One-signer documents: every field is the signer's. Multi-signer: only their own;
  // other signers' fields are shown read-only.
  // Fields behind an unticked checkbox stay hidden and aren't needed.
  const shown = shownFieldIds(sessionView, fieldValues);
  const fields = ownFields(sessionView).filter((f) => shown.has(f.id));
  // Covers for sections that don't apply on this page. A section whose
  // checkbox is itself hidden sits inside a covered section already, so it
  // is only drawn when no other cover contains it.
  const coversFor = (pageNum: number) => {
    const off = (sessionView.conditionalAreas ?? []).filter((a) => a.pageNumber === pageNum && !sectionApplies(a.showIfFieldId));
    const labelled = off.filter((a) => shown.has(a.showIfFieldId));
    const within = (a: ConditionalArea, b: ConditionalArea) =>
      a.posY >= b.posY - 0.5 && a.posY + a.height <= b.posY + b.height + 0.5;
    return [...labelled, ...off.filter((a) => !shown.has(a.showIfFieldId) && !labelled.some((b) => within(a, b)))];
  };
  // A conditional section applies when its checkbox is shown and ticked.
  const sectionApplies = (boxId: string) => shown.has(boxId) &&
    (boxId in fieldValues ? fieldValues[boxId]?.value : doneById.get(boxId)?.value) === 'true';
  const multiSigner = sessionView.signer;
  const doneById = new Map((sessionView.completedFields ?? []).map((c) => [c.fieldId, c]));
  const signerNameAt = (pos?: number) =>
    sessionView.signers?.find((s) => s.position === (pos ?? 1))?.name ?? `Signer ${pos ?? 1}`;
  const filledCount = fields.filter((f) => {
    const val = fieldValues[f.id];
    return f.type === 'SIGNATURE' || f.type === 'INITIALS' ? !!val?.signatureDataUrl : !!val?.value;
  }).length;
  const totalRequired = fields.filter((f) => f.required).length;
  const filledRequired = fields.filter((f) => {
    if (!f.required) return false;
    const val = fieldValues[f.id];
    return f.type === 'SIGNATURE' || f.type === 'INITIALS' ? !!val?.signatureDataUrl : !!val?.value;
  }).length;
  const allRequiredFilled = filledRequired === totalRequired;

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', flexDirection: 'column' }}>
      {/* ── Branded header ─────────────────────────────────────────────── */}
      <header style={{
        background: brandColor, padding: '14px 20px',
        display: 'flex', alignItems: 'center', gap: '12px',
        position: 'sticky', top: 0, zIndex: 50,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        {sessionView.merchant.logoUrl && (
          <img
            src={sessionView.merchant.logoUrl}
            alt={merchantName}
            style={{ height: '28px', objectFit: 'contain', borderRadius: '3px' }}
          />
        )}
        <span style={{ color: '#fff', fontWeight: 700, fontSize: '15px', flex: 1 }}>{merchantName}</span>
        {/* Progress indicator */}
        <span style={{
          background: 'rgba(255,255,255,0.2)', borderRadius: '20px',
          padding: '4px 10px', fontSize: '12px', color: '#fff', fontWeight: 500,
        }}>
          {filledRequired}/{totalRequired} fields
        </span>
      </header>

      {/* ── Document title ─────────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '16px 20px' }}>
        <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827' }}>
          {sessionView.template.name}
        </h1>
        {sessionView.template.description && (
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6b7280', whiteSpace: 'pre-line' }}>
            {sessionView.template.description}
          </p>
        )}
        <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#9ca3af' }}>
          Hi {sessionView.session.signerName} — please review the document below and fill in all highlighted fields.
        </p>
        {multiSigner && (
          <div style={{ margin: '10px 0 0', fontSize: '13px', color: '#374151', background: '#eff6ff', borderRadius: '8px', padding: '10px 12px' }}>
            <strong>You are signer {multiSigner.position} of {multiSigner.total}</strong>
            {multiSigner.role ? ` (${multiSigner.role})` : ''}. Fields for the other signers are shown in grey.
            <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
              {(sessionView.signers ?? []).map((s) => (
                <span key={s.position} style={{ whiteSpace: 'nowrap', fontWeight: s.position === multiSigner.position ? 700 : 400 }}>
                  {s.status === 'SIGNED' ? '✓' : s.position === multiSigner.position ? '✍' : '○'} {s.position}. {s.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── PDF + overlays ─────────────────────────────────────────────── */}
      <main style={{ flex: 1, maxWidth: '880px', width: '100%', margin: '0 auto', padding: '24px 12px 0' }}>
        {/* Inline validation error banner */}
        {validationErrors.size > 0 && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px',
            padding: '12px 16px', marginBottom: '16px', fontSize: '14px', color: '#991b1b',
          }}>
            Please fill in all required fields before signing.
          </div>
        )}

        <div ref={pdfWrapRef}>
          {pdfUrl ? (
            <Document
              file={pdfUrl}
              loading={
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <div style={{ width: 28, height: 28, border: '3px solid #e5e7eb', borderTopColor: brandColor, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                  Loading document…
                </div>
              }
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            >
              {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
                <div
                  key={pageNum}
                  ref={(el) => { pageRefs.current[pageNum] = el; }}
                  style={{
                    position: 'relative',
                    marginBottom: '16px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    lineHeight: 0,
                  }}
                >
                  <Page
                    pageNumber={pageNum}
                    width={pdfWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onRenderSuccess={() => measurePage(pageNum)}
                  />
                  {/* Interactive field overlay */}
                  <div style={{ position: 'absolute', inset: 0 }}>
                    {coversFor(pageNum).map((area) => {
                        const box = sessionView.fields.find((f) => f.id === area.showIfFieldId);
                        return (
                          <SectionCover
                            key={area.id}
                            area={area}
                            boxLabel={box && shown.has(box.id) ? box.label : undefined}
                            mine={!!box && fields.some((f) => f.id === box.id)}
                          />
                        );
                      })}
                    {multiSigner && sessionView.fields
                      .filter((f) => f.pageNumber === pageNum && (f.signerPosition ?? 1) !== multiSigner.position && shown.has(f.id))
                      .map((field) => (
                        <OtherSignerField
                          key={field.id}
                          field={field}
                          done={doneById.get(field.id)}
                          signerName={signerNameAt(field.signerPosition)}
                        />
                      ))}
                    {fields
                      .filter((f) => f.pageNumber === pageNum)
                      .map((field) => (
                        <FieldOverlay
                          key={field.id}
                          field={field}
                          value={fieldValues[field.id]}
                          pageHeightPx={pageHeights[pageNum] ?? 0}
                          brandColor={brandColor}
                          invalid={validationErrors.has(field.id)}
                          onTextChange={onTextChange}
                          onSignatureClick={onSignatureClick}
                        />
                      ))}
                  </div>
                </div>
              ))}
            </Document>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>Loading PDF…</div>
          )}
        </div>

        {/* ── Consent + actions ────────────────────────────────────────── */}
        <div style={{
          background: '#fff', borderRadius: '12px', padding: '24px',
          marginTop: '24px', marginBottom: '32px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          {/* Progress bar */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>
              <span>Document completion</span>
              <span>{filledCount} of {fields.length} field{fields.length !== 1 ? 's' : ''} filled</span>
            </div>
            <div style={{ height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${fields.length ? (filledCount / fields.length) * 100 : 0}%`,
                background: allRequiredFilled ? '#059669' : brandColor,
                borderRadius: '3px',
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>

          {/* Consent */}
          <label
            id="consent-checkbox"
            style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer',
              padding: '14px', background: '#f9fafb', borderRadius: '8px',
              border: `1.5px solid ${consentChecked ? '#059669' : '#e5e7eb'}`,
              marginBottom: '20px', transition: 'border-color 0.2s',
            }}
          >
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              style={{ marginTop: '2px', width: '16px', height: '16px', accentColor: '#059669', flexShrink: 0 }}
            />
            <span style={{ fontSize: '14px', color: '#374151', lineHeight: '1.5' }}>
              I agree to sign this document electronically. I understand that my electronic signature
              is legally binding and has the same legal effect as a handwritten signature under applicable law.
            </span>
          </label>

          {/* Action buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              onClick={handleSubmit}
              disabled={status === 'submitting' || !consentChecked}
              style={{
                width: '100%', padding: '14px',
                background: !consentChecked ? '#9ca3af' : brandColor,
                color: '#fff', border: 'none', borderRadius: '8px',
                fontSize: '16px', fontWeight: 700, cursor: !consentChecked ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'background 0.2s',
              }}
            >
              {status === 'submitting' ? (
                <>
                  <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                  Signing…
                </>
              ) : (
                '✓ Sign Document'
              )}
            </button>

            <button
              onClick={handleDecline}
              disabled={status === 'submitting'}
              style={{
                width: '100%', padding: '12px',
                background: 'transparent', color: '#6b7280',
                border: '1.5px solid #d1d5db', borderRadius: '8px',
                fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                transition: 'border-color 0.2s, color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#EF4444'; e.currentTarget.style.color = '#EF4444'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#6b7280'; }}
            >
              Decline to sign
            </button>
          </div>
        </div>
      </main>

      {/* Powered-by footer — free marketing on every signing page */}
      <footer style={{ textAlign: 'center', padding: '20px 16px 24px', borderTop: '1px solid #e5e7eb', marginTop: '8px', background: '#fff' }}>
        <a href="https://merchantkits.com/signshield" target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '6px', textDecoration: 'none' }}>
          <img src={`${window.location.origin}/logo-full.png`} alt="SignShield" style={{ width: '150px', height: 'auto', opacity: 1 }} />
          <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>Powered by SignShield</span>
        </a>
      </footer>

      {/* ── Signature pad modal ─────────────────────────────────────────── */}
      {signaturePadField && (
        <SignaturePad
          label={signaturePadField.label}
          brandColor={brandColor}
          onConfirm={onSignatureConfirm}
          onCancel={() => setSignaturePadField(null)}
        />
      )}
    </div>
  );
}
